<template>
  <div class="watchlist">
    <ftu-block
      dismiss-key="watchlist"
      icon="visibility"
      title="This is your Watchlist"
      description="With your Watchlist, you can get daily updates on cards you care about. You can add any card to your watchlist from this page or the card's profile. You can also set up price alerts for when the price goes above or below a specific price point."
    />

    <page-header title="Watchlist">
      <template #actions>
        <icon-button
          v-if="showAdd"
          icon="add_circle"
          @click="addModalOpen = true"
        />
      </template>
    </page-header>

    <empty-state
      v-if="showEmptyState"
      icon="style"
      title="You aren't watching any cards!"
      description="Cards can be added by clicking the Add button above"
    />

    <template v-else>
      <align flex collapse-end direction="flex-start">
        <shared-filters
          :filters.sync="filters"
          :query.sync="query"
          :properties="defaultFilters"
          @clear="clearFilters"
        />

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>

      <filter-results
        :is-filtering="hasFilters"
        :filters="filters"
        @clear="clearFilters"
      />

      <div class="results">
        <empty-state
          v-if="showEmptyFilterState"
          icon="search_off"
          title="There are no results with your filters"
          description="Try adjusting your filters"
        />

        <template>
          <div class="list">
            <card-list-item
              v-for="card in watchlistItems"
              :key="card.id"
              :to="{ path: '/watchlist', query: { ...$route.query, cardId: card.cardId, profile: 'watchlist' } }"
              :card="card"
              :fields="itemFields"
              as="router-link"
            >
              <icon-button
                tooltip="Setup price alerts"
                :class="['alert-icon', getAlertIcon(card)]"
                :icon="getAlertIcon(card)"
                @click="setupAlerts(card)"
              />
            </card-list-item>
          </div>

          <infinite-scroll
            :loading="loading"
            :has-more-data="hasMoreData"
            @load="nextPage"
          />
        </template>
      </div>
    </template>

    <watchlist-add-modal
      :open="addModalOpen"
      @close="addModalOpen = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/vue';

import {
  DEFAULT_CARD_FILTERS,
  WATCHLIST_SIZE_LIMIT,
  WATCHLIST_SORT_OPTIONS,
} from '@cardladder/shared/constants';
import { getAlertIcon } from '@cardladder/shared/utils/watchlist.util';
import { searchService } from '@/services';

import CardListItem from '@cardladder/shared/components/CardListItem';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';
import WatchlistAddModal from './WatchlistAddModal';

export default {
  components: {
    CardListItem,
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
    WatchlistAddModal,
  },

  data() {
    return {
      limit: 20,
      query: '',
      loading: false,
      loaded: false,
      page: 1,
      hasMoreData: true,
      addModalOpen: false,
      watchlistItems: [],
      defaultFilters: DEFAULT_CARD_FILTERS,
      sortOptions: WATCHLIST_SORT_OPTIONS,
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  mounted() {
    this.$bus.$on('watchlist-changed', this.handleWatchlistChange);
  },

  beforeDestroy() {
    this.$bus.$off('watchlist-changed', this.handleWatchlistChange);
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.load();
      }
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    showEmptyState() {
      return !this.loading && this.loaded && this.numCardsWatching === 0;
    },

    showEmptyFilterState() {
      return !this.loading && this.loaded && this.hasFilters && this.watchlistItems.length === 0;
    },

    showAdd() {
      return this.numCardsWatching < WATCHLIST_SIZE_LIMIT;
    },

    itemFields() {
      const fields = [...WATCHLIST_SORT_OPTIONS]
        .sort((a, b) => {
          if (a === this.sort) return -1;
          if (b === this.sort) return 1;

          return 0;
        })
        .slice(0, 3)
        .reverse();

      if (fields[1] === 'currentValue') {
        fields[1] = fields[0];
        fields[0] = 'currentValue';
      }

      return fields;
    },

    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.watchlistSort);

        if (activeSort) {
          return this.$store.state.watchlistSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_WATCHLIST_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.watchlistDirection;
      },

      set(value) {
        this.$store.commit('SET_WATCHLIST_DIRECTION', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.watchlistFilters;
      },

      set(value) {
        this.$store.commit('SET_WATCHLIST_FILTERS', value);
      },
    },

    hasFilters() {
      return !isEqual(this.filters, this.defaultFilters);
    },

    numCardsWatching() {
      return this.user.numCardsWatching || 0;
    },
  },

  methods: {
    reset() {
      this.loaded = false;
      this.page = 1;
      this.hasMoreData = true;
      this.load(true);
    },

    async load(resetLoad = false) {
      this.loading = true;

      try {
        const data = await searchService.search({
          index: 'watchlistcards',
          query: this.query.trim(),
          page: this.page,
          limit: this.limit,
          filters: this.filters,
          sort: this.sort,
          direction: this.direction,
        });

        if (resetLoad) {
          this.watchlistItems = data.hits;
        } else {
          this.watchlistItems = [...this.watchlistItems, ...data.hits];
        }

        if (data.hits.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        this.$toasted.error('Error loading the watchlist items');
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },

    getAlertIcon(card) {
      return getAlertIcon(card);
    },

    setupAlerts(card) {
      this.$bus.$emit('watchlist-alert-setup', card);
    },

    handleWatchlistChange(change) {
      if (change.remove) {
        const index = this.watchlistItems.findIndex(({ cardId }) => cardId === change.remove);
        if (index !== -1) this.watchlistItems.splice(index, 1);
      } else if (change.edit) {
        const index = this.watchlistItems.findIndex(({ cardId }) => cardId === change.edit.cardId);
        if (index !== -1) this.$set(this.watchlistItems, index, { ...this.watchlistItems[index], ...change.edit });
      } else if (change.add) {
        setTimeout(() => {
          this.reset();
        }, 5000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.watchlist {
  @include container;
  min-height: 100%;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.alert-icon.notification_important {
  --theme-icon-button-color: #{$color-red-1};
}
</style>
